import { useState, useEffect, useRef, useCallback } from "react";
import defaultSlides from "./slides";
import useMobile from "../../hooks/useMobile";
import chevronLeft from "../../images/icons/blue_chevron_left.svg";
import chevronRight from "../../images/icons/blue_chevron_right.svg";

/**
 * Универсальный слайдер с двумя режимами отображения:
 * - preview=false (по умолчанию): точки внизу
 * - preview=true: превьюшки изображений
 *
 * @param {Object} props
 * @param {Array} props.images - Массив изображений [{url, id?, alt?}] для режима превьюшек
 * @param {Array} props.slides - Массив слайдов [{pathDesktop, pathMobile, title, text}] для режима с описанием
 * @param {boolean} props.preview - Показывать превьюшки вместо точек (default: false)
 * @param {boolean} props.autoPlay - Автопереключение слайдов (default: true)
 * @param {number} props.autoPlayInterval - Интервал автопереключения в мс (default: 5000)
 * @param {boolean} props.showDescription - Показывать title и text из slides (default: true для slides, false для images)
 * @param {string} props.className - Дополнительный CSS класс
 * @param {string} props.alt - Alt текст для изображений (используется если не задан в image)
 */
const Slider = ({
  images,
  slides,
  preview = false,
  autoPlay = true,
  autoPlayInterval = 5000,
  showDescription,
  className = "",
  alt = "Изображение",
}) => {
  const isMobile = useMobile(620);

  // Определяем источник данных
  const isImagesMode = !!images && images.length > 0;
  const slidesData = isImagesMode ? images : slides || defaultSlides;
  const shouldShowDescription =
    showDescription !== undefined ? showDescription : !isImagesMode;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [displayedSlide, setDisplayedSlide] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());
  const [thumbnailErrors, setThumbnailErrors] = useState(new Set());
  const intervalRef = useRef(null);

  // Функция для получения URL изображения
  const getImageSrc = useCallback(
    (slideIndex) => {
      const slide = slidesData[slideIndex];
      if (!slide) return null;

      if (isImagesMode) {
        return slide.url || slide.src;
      }
      return isMobile ? slide.pathMobile : slide.pathDesktop;
    },
    [slidesData, isImagesMode, isMobile]
  );

  // Функция для сброса таймера автосмены
  const resetAutoSlideTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (autoPlay && slidesData.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prevSlide) =>
          prevSlide === slidesData.length - 1 ? 0 : prevSlide + 1
        );
      }, autoPlayInterval);
    }
  }, [autoPlay, autoPlayInterval, slidesData.length]);

  // Авто переключение слайдов
  useEffect(() => {
    resetAutoSlideTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [resetAutoSlideTimer]);

  const switchSlideHandler = (direction) => {
    setCurrentSlide((prevSlide) => {
      if (direction === "prev") {
        return prevSlide === 0 ? slidesData.length - 1 : prevSlide - 1;
      } else {
        return prevSlide === slidesData.length - 1 ? 0 : prevSlide + 1;
      }
    });
    resetAutoSlideTimer();
  };

  const clickDotHandler = (index) => {
    setCurrentSlide(index);
    resetAutoSlideTimer();
  };

  const selectThumbnail = (index) => {
    setCurrentSlide(index);
    resetAutoSlideTimer();
  };

  // Функция для предзагрузки изображения
  const preloadImage = useCallback(
    (src) => {
      return new Promise((resolve, reject) => {
        if (!src || preloadedImages.has(src)) {
          resolve();
          return;
        }

        const img = new Image();
        img.onload = () => {
          setPreloadedImages((prev) => new Set([...prev, src]));
          resolve();
        };
        img.onerror = reject;
        img.src = src;
      });
    },
    [preloadedImages]
  );

  // Сбрасываем состояние загрузки при смене слайда
  useEffect(() => {
    const currentImageSrc = getImageSrc(currentSlide);
    if (!currentImageSrc) return;

    // Если изображение уже загружено, сразу показываем его
    if (loadedImages.has(currentImageSrc)) {
      setIsImageLoaded(true);
      setHasImageError(false);
      setDisplayedSlide(currentSlide);
    } else {
      setIsImageLoaded(false);
      setHasImageError(false);
    }

    // Предзагружаем соседние слайды
    const nextSlide = (currentSlide + 1) % slidesData.length;
    const prevSlide =
      currentSlide === 0 ? slidesData.length - 1 : currentSlide - 1;

    preloadImage(getImageSrc(nextSlide));
    preloadImage(getImageSrc(prevSlide));
  }, [
    currentSlide,
    slidesData.length,
    loadedImages,
    getImageSrc,
    preloadImage,
  ]);

  // Обновляем отображаемый слайд после загрузки изображения
  useEffect(() => {
    if (isImageLoaded) {
      setDisplayedSlide(currentSlide);
    }
  }, [isImageLoaded, currentSlide]);

  // Приоритетная предзагрузка первого слайда и соседних
  useEffect(() => {
    if (slidesData.length === 0) return;

    const firstSlideSrc = getImageSrc(0);

    preloadImage(firstSlideSrc)
      .then(() => {
        if (slidesData.length > 1) {
          preloadImage(getImageSrc(1));
        }
        if (slidesData.length > 2) {
          preloadImage(getImageSrc(slidesData.length - 1));
        }
      })
      .catch(() => {});

    // Остальные слайды загружаем в фоне с задержкой
    setTimeout(() => {
      slidesData.forEach((_, index) => {
        if (index !== 0 && index !== 1 && index !== slidesData.length - 1) {
          preloadImage(getImageSrc(index));
        }
      });
    }, 1000);
  }, [slidesData, getImageSrc, preloadImage]);

  if (!slidesData || slidesData.length === 0) {
    return null;
  }

  const current = slidesData[currentSlide];
  const displayed = slidesData[displayedSlide];
  const imageSrc = getImageSrc(currentSlide);
  const displayedImageSrc = getImageSrc(displayedSlide);
  const imageAlt = isImagesMode ? current?.alt || alt : displayed?.title || alt;

  return (
    <div
      className={`slider ${
        preview ? "slider--preview" : ""
      } ${className} w-100`}
    >
      {/* Описание (только для режима slides с showDescription) */}
      {isImageLoaded && shouldShowDescription && !isImagesMode && (
        <div className="description-controller">
          <div className="description flex-column gap-3x">
            <h1>{displayed?.title}</h1>
            <p>{displayed?.text}</p>
          </div>
        </div>
      )}

      {/* Контейнер изображения с навигацией */}
      <div className="banner-container flex-row flex-center">
        {/* Кнопки навигации внутри banner-container для центрирования по высоте изображения */}
        {isImageLoaded && slidesData.length > 1 && (
          <div className="navigation">
            <div
              className="navigate-button navigate-prev"
              onClick={() => switchSlideHandler("prev")}
            >
              <img
                className="icon icon-center"
                src={chevronLeft}
                alt="Предыдущее"
              />
            </div>
            <div
              className="navigate-button navigate-next"
              onClick={() => switchSlideHandler("next")}
            >
              <img
                className="icon icon-center"
                src={chevronRight}
                alt="Следующее"
              />
            </div>
          </div>
        )}

        {!isImageLoaded && !hasImageError && (
          <div className="slider-loading">
            <span>Загрузка изображения...</span>
          </div>
        )}
        {hasImageError && (
          <div className="slider-error">
            <span>Не удалось загрузить изображение</span>
          </div>
        )}
        {displayedImageSrc && !hasImageError && (
          <img
            className={`banner-image ${isImageLoaded ? "loaded" : "loading"}`}
            src={displayedImageSrc}
            alt={imageAlt}
            onLoad={() => {
              setLoadedImages((prev) => new Set([...prev, imageSrc]));
              setIsImageLoaded(true);
              setHasImageError(false);
              setDisplayedSlide(currentSlide);
            }}
            onError={() => {
              setIsImageLoaded(true);
              setHasImageError(true);
            }}
          />
        )}
      </div>

      {/* Точки (если preview=false) */}
      {!preview && isImageLoaded && slidesData.length > 1 && (
        <div className="dots flex-row">
          {slidesData.map((_, index) => (
            <div
              key={index}
              className={`dot ${index === currentSlide ? "active" : ""}`}
              onClick={() => clickDotHandler(index)}
            ></div>
          ))}
        </div>
      )}

      {/* Превьюшки (если preview=true) */}
      {preview && slidesData.length > 1 && (
        <div className="slider-thumbnails">
          {slidesData.map((slide, index) => {
            const thumbnailUrl = isImagesMode
              ? slide.url || slide.src
              : isMobile
              ? slide.pathMobile
              : slide.pathDesktop;
            const isThumbnailLoaded = loadedThumbnails.has(thumbnailUrl);
            const hasThumbnailError = thumbnailErrors.has(thumbnailUrl);

            return (
              <div
                key={slide.id || index}
                className={`slider-thumbnail ${
                  index === currentSlide ? "active" : ""
                }`}
                onClick={() => selectThumbnail(index)}
              >
                {!isThumbnailLoaded && !hasThumbnailError && (
                  <div className="slider-thumbnail-loading">
                    <span>...</span>
                  </div>
                )}
                {!hasThumbnailError && (
                  <img
                    src={thumbnailUrl}
                    alt={`${imageAlt} - ${index + 1}`}
                    className={isThumbnailLoaded ? "loaded" : "loading"}
                    onLoad={() => {
                      setLoadedThumbnails(
                        (prev) => new Set([...prev, thumbnailUrl])
                      );
                      setThumbnailErrors((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(thumbnailUrl);
                        return newSet;
                      });
                    }}
                    onError={() => {
                      setThumbnailErrors(
                        (prev) => new Set([...prev, thumbnailUrl])
                      );
                      setLoadedThumbnails((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(thumbnailUrl);
                        return newSet;
                      });
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Slider;
